import {
  parseISO,
  isSameDay,
  isSameMonth,
  isSameYear,
  setHours,
  setMinutes,
  setSeconds,
  formatISO,
  isTomorrow,
  format,
  isToday,
  isYesterday,
  isBefore,
  isAfter,
  addYears,
  addMinutes,
} from 'date-fns';
import { DatetimeHighlightStyle } from '@ionic/core';
import { DateTime } from 'luxon';
import { RRule } from 'rrule';
import { TaskDO } from '../../../utils/state/model/interfaces/displayObjects';
import {
  expandClampedMonthlyDates,
  implementLocalDateToUtc,
  implementUtcDateToLocal,
  needsMonthDayClamping,
} from './recurrenceUtils';
import i18n from '../../../i18n';
import { formatTime } from '../../timeTracking/utils/timeUtils';
type CalendarISODate = string | undefined | null | string[];

// Helper function to set initial calendar date
export const initialCalendarDate = (tasksArr: TaskDO[]): string | null => {
  const compareArr = tasksArr.filter((task) => task.dueDate?.valueOf() === tasksArr[0].dueDate?.valueOf());

  if (tasksArr.length === compareArr.length && tasksArr[0]?.dueDate) {
    return formatISO(offsetDateToLocalTime(tasksArr[0].dueDate, tasksArr[0].timezone as string | null));
  }

  return null;
};

/**
 * How far ahead the calendar highlights upcoming occurrences. CalendarView derives its `max` bound
 * from this constant rather than repeating the number, so the user can always browse to every
 * occurrence we highlight.
 */
export const CALENDAR_WINDOW_IN_YEARS = 5;

/**
 * Expands a recurrence rule into the date strings the calendar highlights, from the next
 * occurrence through the next few years. Shared by the initial state and by every later rule
 * change so the highlighted dates can never disagree with the due date we calculate from the
 * same rule.
 *
 * Both branches cut off at `now` rather than the start of today, which is what the due date
 * calculation does. Cutting off at the start of today instead would highlight an occurrence
 * that already elapsed earlier today, on a day the due date has moved past.
 */
export const recurrenceDatesForCalendar = (rule: RRule, tzid: string | null): string[] => {
  const now = new Date();
  const windowEnd = addYears(now, CALENDAR_WINDOW_IN_YEARS);

  const dates = needsMonthDayClamping(rule)
    ? expandClampedMonthlyDates(rule, windowEnd).filter((date) => isAfter(date, now))
    : rule
        .between(implementLocalDateToUtc(now), implementLocalDateToUtc(windowEnd))
        .map((date) => implementUtcDateToLocal(date));

  return dates.map((date) => formatISO(offsetDateToLocalTime(date, tzid), { representation: 'date' }));
};

export const initialHighlightedDates = (tasksArr: TaskDO[]) => {
  if (!initialCalendarDate(tasksArr)) return null;
  if (!tasksArr[0].recurrence) return null;

  const rule = RRule.fromString(tasksArr[0].recurrence);
  return recurrenceDatesForCalendar(rule, rule.options.tzid);
};

/**
 * Builds the callback ion-datetime's `highlightedDates` invokes for every day it renders. Backed by
 * a Set rather than scanning the array, because the callback runs once per rendered day and a daily
 * rule fills the window with well over a thousand dates.
 *
 * ion-datetime skips the callback for the selected day, so the due date keeps its normal selected
 * styling and can never end up carrying both.
 */
export const recurrenceHighlight = (
  dates: string[] | null
): ((dateIsoString: string) => DatetimeHighlightStyle | undefined) | undefined => {
  if (!dates?.length) return undefined;

  const highlighted = new Set(dates);

  return (dateIsoString: string) =>
    highlighted.has(dateIsoString.split('T')[0])
      ? {
          textColor: 'var(--calendar-recurrence-highlight-text)',
          backgroundColor: 'var(--calendar-recurrence-highlight-background)',
        }
      : undefined;
};

export const initialRecurrenceState = (tasksArr: TaskDO[]) => {
  if (!initialCalendarDate(tasksArr)) return null;
  if (!tasksArr[0].recurrence) return null;

  const rule = RRule.fromString(tasksArr[0].recurrence);
  return rule;
};

export const initialRecurrenceOptionState = (tasksArr: TaskDO[]) => {
  if (!tasksArr[0].recurrence) return 'none';

  const rule = RRule.fromString(tasksArr[0].recurrence);

  if (rule.origOptions.interval) return 'custom';

  switch (rule.origOptions.freq) {
    case RRule.DAILY:
      return 'daily';
    case RRule.WEEKLY:
      return rule.options.byweekday.length > 1 ? 'weekdays' : 'weekly';
    case RRule.MONTHLY:
      return rule.origOptions.byweekday ? 'monthly-weekday' : 'monthly';
    case RRule.YEARLY:
      return 'yearly';
  }
  return 'none';
};

// Helper function to set the initial
export const initialAddTimeState = (tasksArr: TaskDO[]) => {
  const date = initialCalendarDate(tasksArr);
  return date && !isDefaultTime(parseISO(date)) ? true : false;
};

export const initialTimezoneState = (tasksArr: TaskDO[]): string => {
  const compareArr = tasksArr.filter((task) => task.timezone === tasksArr[0].timezone);

  if (tasksArr.length === compareArr.length && tasksArr[0]?.timezone) {
    return tasksArr[0].timezone;
  }

  return DateTime.local().zoneName!;
};

// Helper funtion to disable past dates in the calendar
export const isCalendarInteractive = (dateString: string) => {
  const calendarDate = parseISO(dateString);
  const currentDate = new Date();
  return !isBefore(calendarDate, currentDate) || isSameDay(calendarDate, currentDate);
};

// Check if two dates are the same without taking time into account
export const areSameDates = (date1: CalendarISODate, date2: CalendarISODate) => {
  const dateOneObject = parseISO(date1 as string);
  const dateTwoObject = parseISO(date2 as string);

  if (
    isSameDay(dateOneObject, dateTwoObject) &&
    isSameMonth(dateOneObject, dateTwoObject) &&
    isSameYear(dateOneObject, dateTwoObject)
  ) {
    return true;
  }

  return false;
};

// Helper function to offset the date to display the correct time
export const offsetDateToLocalTime = (date: Date, tzid: string | null): Date => {
  if (tzid === null || tzid === DateTime.local().zoneName) return date;
  const taskTimezoneOffset = DateTime.fromJSDate(date).setZone(tzid).offset;
  const localOffset = new Date().getTimezoneOffset();
  const offset = taskTimezoneOffset + localOffset;
  return addMinutes(date, offset);
};

// Helper function to set a default time to the date(11:59:59pm)
export const setDefaultTime = (date: Date) => {
  const defaultHour = setHours(date, 23);
  const defaultMinutes = setMinutes(defaultHour, 59);
  const defaultSeconds = setSeconds(defaultMinutes, 59);
  return formatISO(defaultSeconds);
};

export const updateCustomTime = (calendarDate: Date, timePickerDate: Date) => {
  const defaultHour = setHours(calendarDate, timePickerDate.getHours());
  const defaultMinutes = setMinutes(defaultHour, timePickerDate.getMinutes());
  const defaultSeconds = setSeconds(defaultMinutes, timePickerDate.getSeconds());
  return formatISO(defaultSeconds);
};

export const setCustomTime = (date: Date) => {
  if (!isDefaultTime(date)) return formatISO(date);
  const defaultHour = setHours(date, 12);
  const defaultMinutes = setMinutes(defaultHour, 0);
  const defaultSeconds = setSeconds(defaultMinutes, 0);
  return formatISO(defaultSeconds);
};

export const isDefaultTime = (date: Date) => {
  return date.getHours() === 23 && date.getMinutes() === 59 && date.getSeconds() === 59;
};

//Check when if the due date is ...
export const shouldDisplayNaturalText = (dueDate: Date) => {
  if (isToday(dueDate)) return `${i18n.t('today')} ${!isDefaultTime(dueDate) ? `${formatTime(dueDate)}` : ''}`;
  if (isTomorrow(dueDate)) return `${i18n.t('tomorrow')} ${!isDefaultTime(dueDate) ? `${formatTime(dueDate)}` : ''}`;
  if (isYesterday(dueDate)) return `${i18n.t('yesterday')} ${!isDefaultTime(dueDate) ? `${formatTime(dueDate)}` : ''}`;
};

//Due remove the time from the due date text if is the default time
export const dueDateText = (date: Date, tzid: string | null = null) => {
  const dueDate = offsetDateToLocalTime(date, tzid);
  const displayTimezoneText = tzid && tzid !== DateTime.local().zoneName;
  const timeZoneText = tzid ? DateTime.local().setZone(tzid).offsetNameShort : '';

  if (shouldDisplayNaturalText(dueDate))
    return shouldDisplayNaturalText(dueDate) + (displayTimezoneText ? ` ${timeZoneText}` : '');

  if (dueDate.getHours() === 23 && dueDate.getMinutes() === 59 && dueDate.getSeconds() === 59)
    return format(dueDate, 'L/dd/yy') + (displayTimezoneText ? ` ${timeZoneText}` : '');

  return format(dueDate, 'L/dd/yy hh:mm a') + (displayTimezoneText ? ` ${timeZoneText}` : '');
};

// Check if task is past due date
export const dueDateTextStyle = (task: TaskDO) => {
  // For incompleted tasks
  if (!task.isCompleted && task.dueDate) {
    return isBefore(task.dueDate, new Date()) ? 'error' : 'heavy';
  }

  //TODO: This works but we still need to way for the backend to display the compledtedDate property
  // For completed tasks
  // if(task.isCompleted && task.dueDate){
  //   return isBefore(parseISO(task.dueDate as string), fromUnixTime(task.completedStatus[0].when / 1000)) ? 'error' : 'heavy'
  // }

  return 'light';
};

export const getNthFromDate = (date: Date) => {
  return Math.ceil(date.getDate() / 7);
};
